import { useEffect, useRef, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useCurrentUser, useLogout } from '@/hooks/useAuth';

export function UserMenu({
  collapsed = false,
  variant = 'sidebar',
}: {
  collapsed?: boolean;
  variant?: 'sidebar' | 'navbar' | 'avatar';
}) {
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const name = user?.name || user?.email || 'Admin';
  const email = user?.email ?? '';
  const roleLabel = user?.is_super_admin || user?.role_id === 1 ? 'Super Admin' : 'Admin';

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const signOut = () => {
    logout.mutate(undefined, {
      onSettled: () => {
        setOpen(false);
        void router.navigate({ to: '/login' as never });
      },
    });
  };

  return (
    <div
      className={`user-menu variant-${variant} ${open ? 'open' : ''} ${collapsed ? 'is-collapsed' : ''}`}
      ref={rootRef}
    >
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={variant === 'avatar' ? `Account menu for ${name}` : undefined}
      >
        <span className="user-avatar" aria-hidden>
          {initials(name)}
        </span>
        {variant !== 'avatar' ? (
          <span className="user-meta">
            <span className="user-name">{variant === 'navbar' ? roleLabel : name}</span>
            {email ? <span className="user-email">{email}</span> : null}
          </span>
        ) : null}
        {variant !== 'avatar' ? (
          <span className={`chevron ${open ? 'up' : 'down'}`} aria-hidden>
            ▾
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="user-menu-panel" role="menu">
          <div className="user-menu-header">
            <span className="user-avatar lg" aria-hidden>
              {initials(name)}
            </span>
            <div>
              <div className="user-name">{name}</div>
              {email ? <div className="user-email">{email}</div> : null}
            </div>
          </div>
          <button
            type="button"
            className="user-menu-item danger"
            role="menuitem"
            onClick={signOut}
            disabled={logout.isPending}
          >
            {logout.isPending ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return (parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || 'A').slice(0, 2);
}
